import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { CheckCircle2, Mail, RefreshCw, AlertCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

interface RegistrationSuccessProps {
  email: string;
}

export default function RegistrationSuccessPage({ email }: RegistrationSuccessProps) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleResendEmail = async () => {
    if (resendCooldown > 0 || isResending) return;

    setIsResending(true);
    try {
      const response = await apiRequest("POST", "/api/resend-verification", { email });
      const data = await response.json();
      
      if (data.success !== false) {
        setResendCooldown(60);
        toast({
          title: "Email odoslaný",
          description: "Overovací email bol znovu odoslaný na vašu adresu.",
        });
      } else {
        toast({
          title: "Chyba",
          description: data.message || "Nepodarilo sa odoslať email.",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      toast({
        title: "Chyba",
        description: error.message || "Nepodarilo sa odoslať email.",
        variant: "destructive",
      });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
            <CheckCircle2 className="w-10 h-10 text-green-600 dark:text-green-400" />
          </div>
          <CardTitle className="text-3xl">Ďakujeme za registráciu!</CardTitle>
          <CardDescription className="text-lg">
            Poslali sme vám overovací email na adresu
          </CardDescription>
          <div className="flex items-center justify-center gap-2 text-lg font-semibold text-primary">
            <Mail className="w-5 h-5" />
            <span>{email}</span>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Čo ďalej sekcia */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 space-y-4">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <ArrowRight className="w-5 h-5" />
              Čo ďalej?
            </h3>
            <ol className="space-y-3 list-decimal list-inside">
              <li className="text-muted-foreground">
                Otvorte svoj email a nájdite správu od TaskKrow
              </li>
              <li className="text-muted-foreground">
                Kliknite na tlačidlo <strong>"Potvrdiť účet"</strong> v emaile
              </li>
              <li className="text-muted-foreground">
                Vráťte sa sem a prihláste sa do svojho účtu
              </li>
            </ol>
          </div>

          {/* Resend email sekcia */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={handleResendEmail}
              disabled={resendCooldown > 0 || isResending}
              variant="outline"
              className="flex-1"
            >
              {isResending ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Odosiela sa...
                </>
              ) : resendCooldown > 0 ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Znovu odoslať ({resendCooldown}s)
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Preposlať overovací email
                </>
              )}
            </Button>
            <Button
              onClick={() => setLocation("/auth")}
              className="flex-1"
            >
              Prejsť na prihlásenie
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          {/* Tipy sekcia */}
          <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 border border-amber-200 dark:border-amber-800">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5" />
              <div className="space-y-2 flex-1">
                <h4 className="font-semibold text-sm">Neprišiel email?</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Skontrolujte priečinok <strong>Spam</strong> alebo <strong>Nevyžiadaná pošta</strong></li>
                  <li>• Overte, či ste zadali správnu emailovú adresu</li>
                  <li>• Počkajte niekoľko minút - email môže prísť s oneskorením</li>
                  <li>• Ak problém pretrváva, kontaktujte podporu na <strong>support@taskkrow.click</strong></li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
